import * as React from 'react';
import { Box, Grid, ThemeProvider, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { generateTheme } from '../themes';
import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';

const clampColumns = (n) => {
    const v = parseInt(n, 10);
    if (v === 2 || v === 4) return v;
    return 3;
};

const KpiGridSlice = ({ data }) => {
    const {
        colorTheme = 'white',
        eyebrow,
        title,
        tagline,
        columns = 3,
        kpis = []
    } = data;

    const theme = generateTheme(colorTheme);
    const containerRef = useRef(null);
    const cols = clampColumns(columns);

    useLayoutEffect(() => {
        if (!containerRef.current) return;

        const kpiEls = gsap.utils.toArray('.kg-value', containerRef.current);
        kpiEls.forEach((el, i) => {
            const target = parseFloat(el.dataset.target);
            if (Number.isNaN(target)) return;
            const counter = { val: 0 };
            gsap.to(counter, {
                val: target,
                duration: 1.4,
                delay: 0.1 * i,
                ease: 'power2.out',
                onUpdate: () => {
                    el.textContent = Math.round(counter.val);
                },
                scrollTrigger: {
                    id: 'kgValue',
                    trigger: el,
                    start: 'top 85%',
                    markers: false
                }
            });
        });

        const cards = gsap.utils.toArray('.kg-card', containerRef.current);
        cards.forEach((el, i) => {
            gsap.fromTo(
                el,
                { opacity: 0, y: 12 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.7,
                    delay: 0.08 * i,
                    ease: 'easeIn',
                    scrollTrigger: {
                        id: 'kgCard',
                        trigger: el,
                        start: 'top 85%',
                        markers: false
                    }
                }
            );
        });
    }, [kpis.length, cols]);

    // MUI Grid's xs=12 means one-column on mobile. md= 12/cols for desktop.
    // For 2 cols on tablet (sm), use sm={6} when desktop is 3/4 cols.
    const mdSpan = 12 / cols;
    const smSpan = cols === 2 ? 12 : 6;

    return (
        <ThemeProvider theme={theme}>
            <Box
                ref={containerRef}
                sx={{
                    background: (t) => t.palette.background.default,
                    px: { xs: 2, md: 12 },
                    py: { xs: 4, md: 6 },
                    textAlign: 'center'
                }}
            >
                {eyebrow && (
                    <Typography
                        variant="overline"
                        component="span"
                        sx={{
                            display: 'block',
                            color: 'text.secondary',
                            letterSpacing: '0.15em',
                            fontSize: '0.8rem',
                            fontWeight: 400,
                            opacity: 0.6,
                            mb: { xs: 1, md: 1.5 }
                        }}
                        dangerouslySetInnerHTML={{ __html: eyebrow }}
                    />
                )}

                {title && (
                    <Typography
                        component="h3"
                        sx={{
                            color: 'text.primary',
                            fontFamily: 'Kanit, sans-serif',
                            fontWeight: 800,
                            fontSize: { xs: '1.75rem', md: '2.25rem', lg: '2.75rem' },
                            lineHeight: 1.15,
                            letterSpacing: '-0.01em',
                            maxWidth: 820,
                            mx: 'auto'
                        }}
                        dangerouslySetInnerHTML={{ __html: title }}
                    />
                )}

                {tagline && (
                    <Typography
                        component="p"
                        sx={{
                            color: 'text.secondary',
                            fontSize: { xs: '0.95rem', md: '1.05rem' },
                            lineHeight: 1.55,
                            maxWidth: 620,
                            mx: 'auto',
                            mt: { xs: 1.5, md: 2 }
                        }}
                        dangerouslySetInnerHTML={{ __html: tagline }}
                    />
                )}

                {kpis.length > 0 && (
                    <Grid
                        container
                        spacing={{ xs: 3, md: 4 }}
                        sx={{
                            mt: { xs: 4, md: 6 },
                            justifyContent: 'center'
                        }}
                    >
                        {kpis.map((kpi, i) => (
                            <Grid
                                item
                                xs={12}
                                sm={smSpan}
                                md={mdSpan}
                                key={i}
                                className="kg-card"
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        px: { xs: 1, md: 2 }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            justifyContent: 'center',
                                            gap: 0.5,
                                            lineHeight: 1
                                        }}
                                    >
                                        <Box
                                            component="span"
                                            className="kg-value"
                                            data-target={kpi.value}
                                            sx={{
                                                color: 'text.primary',
                                                fontFamily: 'Kanit, sans-serif',
                                                fontWeight: 900,
                                                fontSize: { xs: '2.75rem', md: '3.25rem', lg: '3.75rem' },
                                                lineHeight: 1,
                                                letterSpacing: '-0.02em'
                                            }}
                                        >
                                            0
                                        </Box>
                                        {kpi.unit && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    color: 'text.primary',
                                                    fontFamily: 'Kanit, sans-serif',
                                                    fontWeight: 700,
                                                    fontSize: { xs: '1.1rem', md: '1.25rem', lg: '1.4rem' },
                                                    letterSpacing: '0.02em'
                                                }}
                                            >
                                                {kpi.unit}
                                            </Box>
                                        )}
                                    </Box>

                                    {(kpi.label || kpi.tooltip) && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 0.75,
                                                mt: 0.75,
                                                maxWidth: 280
                                            }}
                                        >
                                            {kpi.label && (
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontSize: { xs: '0.9rem', md: '0.95rem' },
                                                        lineHeight: 1.4
                                                    }}
                                                >
                                                    {kpi.label}
                                                </Typography>
                                            )}
                                            {kpi.tooltip && (
                                                <Tooltip
                                                    title={kpi.tooltip}
                                                    arrow
                                                    enterTouchDelay={0}
                                                    leaveTouchDelay={4000}
                                                    placement="top"
                                                >
                                                    <InfoOutlinedIcon
                                                        sx={{
                                                            fontSize: '1.05rem',
                                                            color: 'text.secondary',
                                                            opacity: 0.85,
                                                            cursor: 'help',
                                                            flexShrink: 0,
                                                            transition: 'opacity 0.2s',
                                                            '&:hover': { opacity: 1 }
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </ThemeProvider>
    );
};

export default KpiGridSlice;
